/**
 * Entry booster enforcement + upsell wiring.
 *
 * Works in both the cart drawer and the cart page. Booster variant ids come
 * from the JSON emitted by snippets/ff-entry-boosters.liquid (driven by
 * Theme settings > Entry boosters) so there is one source of truth.
 *
 * Rule: boosters never stack. Exactly one booster line, quantity 1, may be in
 * the cart. The booster the shopper picked most recently wins — whether it was
 * tapped in the drawer or added from its own product page — otherwise the
 * highest multiplier.
 *
 * One round trip per change: the cart update request also renders the drawer,
 * the cart page, the header bubble and the header entries chip, so nothing
 * else has to fetch the cart again afterwards.
 */
(() => {
  // The cart page and the drawer can both request this file; run once only.
  if (window.__ffMultipliersInit) return;
  window.__ffMultipliersInit = true;

  const BOOST_SELECTOR = '.boost-button';
  const CONFIG_SELECTOR = '[data-ff-boosters-config]';
  const LAST_PICK_KEY = 'ff_last_booster_variant';
  const SEEN_KEY = 'ff_boosters_seen';
  const ENFORCE_HEADER = 'X-FF-Multiplier-Enforce';

  // Legacy promo cleanup: when a discounted 10X line and a full-price 10X line
  // coexist, drop the full-price one. Kept from the original implementation.
  const TEN_X_TITLE_MATCH = '10X ENTRY BOOSTER';

  // Dawn posts to /cart/add (no .js) from product forms; apps often use .js.
  const CART_ENDPOINT_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?.*)?$/;
  const CART_ADD_RE = /\/cart\/add(\.js)?(\?.*)?$/;

  let isNormalizing = false;

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const getButtons = () => qsa(BOOST_SELECTOR);
  const num = (value) => Number(value) || 0;

  /* ---------------------------------------------------------------- config */

  function readBoosters() {
    const node = document.querySelector(CONFIG_SELECTOR);
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed.filter((b) => num(b.id)) : [];
    } catch (err) {
      console.error('[ff-multipliers] could not parse booster config', err);
      return [];
    }
  }

  const boosterIds = () => readBoosters().map((b) => num(b.id));

  const multiplierById = () => new Map(readBoosters().map((b) => [num(b.id), num(b.mult)]));

  /* -------------------------------------------------------------- memory */

  function readStore(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (err) {
      /* private mode or blocked storage; the highest multiplier still wins */
    }
  }

  const lastPick = () => num(readStore(LAST_PICK_KEY));

  function setLastPick(variantId) {
    writeStore(LAST_PICK_KEY, variantId ? String(variantId) : null);
  }

  function readSeen() {
    try {
      const parsed = JSON.parse(readStore(SEEN_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.map(num) : [];
    } catch (err) {
      return [];
    }
  }

  /** Remember which boosters the cart holds so a newly added one can be told apart. */
  function rememberSeen(ids) {
    writeStore(SEEN_KEY, JSON.stringify(ids.map(num)));
  }

  const boostersIn = (cart, ids) =>
    (Array.isArray(cart?.items) ? cart.items : []).filter(
      (it) => ids.includes(num(it.variant_id)) && num(it.quantity) > 0
    );

  /* ------------------------------------------------------------- cart api */

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error(`cart.js failed: ${res.status}`);
    return res.json();
  }

  /**
   * Containers on this page that can be re-rendered from a cart response.
   * Mirrors Dawn's own getSectionsToRender() contracts.
   */
  function getRenderTargets() {
    const targets = [];

    const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (drawerInner) {
      targets.push({ section: 'cart-drawer', selector: '.drawer__inner', el: drawerInner });
    }

    const mainItems = document.getElementById('main-cart-items');
    const mainContents = mainItems && mainItems.querySelector('.js-contents');
    if (mainItems && mainContents && mainItems.dataset.id) {
      targets.push({ section: mainItems.dataset.id, selector: '.js-contents', el: mainContents });
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      targets.push({ section: 'cart-icon-bubble', selector: '.shopify-section', el: bubble });
    }

    const chip = document.querySelector('ff-header-entries');
    if (chip) {
      targets.push({ section: 'ff-header-entries', selector: 'ff-header-entries', el: chip, chip: true });
    }

    return targets;
  }

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  function applyChip(target, found) {
    const changed = found.dataset.total !== target.el.dataset.total;
    target.el.dataset.total = found.dataset.total || '';
    target.el.innerHTML = found.innerHTML;
    if (!changed) return;
    const chip = target.el.querySelector('.ff-header-chip');
    if (!chip) return;
    chip.classList.remove('ff-header-chip--bump');
    void chip.offsetWidth;
    chip.classList.add('ff-header-chip--bump');
  }

  function syncDrawerEmptyState(doc) {
    const rendered = doc.querySelector('cart-drawer');
    const drawer = document.querySelector('cart-drawer');
    if (!rendered || !drawer) return;
    const empty = rendered.classList.contains('is-empty');
    drawer.classList.toggle('is-empty', empty);
    const inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', empty);
  }

  function applySections(sections, targets) {
    if (!sections) return false;
    let applied = false;

    for (const target of targets) {
      const html = sections[target.section];
      if (!html) continue;
      const doc = parseHTML(html);
      const found = doc.querySelector(target.selector);
      if (!found) continue;

      if (target.chip) applyChip(target, found);
      else target.el.innerHTML = found.innerHTML;
      if (target.section === 'cart-drawer') syncDrawerEmptyState(doc);
      applied = true;
    }

    return applied;
  }

  function flagEntriesUpdated() {
    for (const el of qsa('[data-ff-entries-total]')) {
      el.classList.remove('is-updated');
      // Force reflow so the animation restarts on repeated updates.
      void el.offsetWidth;
      el.classList.add('is-updated');
    }
  }

  function setPending(pending) {
    for (const el of qsa('[data-ff-entries-total], [data-ff-boosters]')) {
      el.classList.toggle('is-pending', pending);
    }
  }

  const formatCount = (value) => Math.max(0, Math.round(value)).toLocaleString('en-US');

  /**
   * Show the new total straight away; the server render replaces it moments
   * later with the same figure. Base entries come from the block's own data.
   */
  function previewEntries(multiplier) {
    const mult = Math.max(1, num(multiplier));
    for (const block of qsa('[data-ff-entries-total]')) {
      const base = num(block.dataset.baseEntries);
      const count = block.querySelector('.entries-total__count');
      if (count) count.textContent = formatCount(base * mult);
    }

    const chip = document.querySelector('ff-header-entries');
    const chipLink = chip && chip.querySelector('.ff-header-chip');
    const baseBlock = document.querySelector('[data-ff-entries-total]');
    if (!chipLink || !baseBlock) return;
    const total = num(baseBlock.dataset.baseEntries) * mult;
    chipLink.textContent = chipLink.textContent.replace(/^\s*[\d,.\s]+/, `${formatCount(total)} `);
    chip.dataset.total = String(total);
  }

  /**
   * Update the cart and refresh the affected sections in place, in one request.
   * Falls back to a reload only if no container could be re-rendered.
   */
  async function cartUpdate(updates) {
    const targets = getRenderTargets();
    const sections = targets.map((t) => t.section).filter(Boolean);

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [ENFORCE_HEADER]: '1',
      },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({
        updates,
        sections,
        sections_url: window.location.pathname,
      }),
    });

    if (!res.ok) throw new Error(`cart/update.js failed: ${res.status}`);

    const data = await res.json();
    const applied = applySections(data.sections, targets);

    if (applied) {
      flagEntriesUpdated();
      document.dispatchEvent(new CustomEvent('ff:entries:updated', { detail: { cart: data } }));
    } else if (sections.length) {
      // Sections were requested but nothing rendered — stay consistent.
      window.location.reload();
    }

    rememberSeen(boostersIn(data, boosterIds()).map((it) => num(it.variant_id)));
    return data;
  }

  /* ------------------------------------------------------------------- ui */

  function setActiveUI(variantId) {
    for (const btn of getButtons()) {
      const active = num(btn.dataset.variantId) === num(variantId);
      btn.classList.toggle('selected', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
  }

  function clearActiveUI() {
    setActiveUI(null);
  }

  function showError(btn, message) {
    const wrap = btn.closest('[data-ff-boosters]');
    if (!wrap) return;
    let node = wrap.querySelector('.ff-boosters__error');
    if (!node) {
      node = document.createElement('p');
      node.className = 'ff-boosters__error';
      node.setAttribute('role', 'alert');
      wrap.appendChild(node);
    }
    node.textContent = message;
  }

  function clearError(btn) {
    const wrap = btn.closest('[data-ff-boosters]');
    const node = wrap && wrap.querySelector('.ff-boosters__error');
    if (node) node.remove();
  }

  function setBusy(btn, busy) {
    btn.disabled = busy;
    if (busy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
    setPending(busy);
  }

  /* ---------------------------------------------------------- enforcement */

  const highestOf = (items, multById) =>
    num(
      [...items].sort((a, b) => multById.get(num(b.variant_id)) - multById.get(num(a.variant_id)))[0]
        ?.variant_id
    );

  /**
   * Which booster should survive. A booster that was not in the cart the last
   * time we looked is the shopper's newest choice and wins outright; otherwise
   * the most recent drawer pick; otherwise the highest multiplier.
   */
  function chooseWinnerVariantId(inCart, multById) {
    if (!inCart.length) return null;

    const seen = readSeen();
    const newcomers = inCart.filter((it) => !seen.includes(num(it.variant_id)));
    if (newcomers.length && newcomers.length < inCart.length) {
      return highestOf(newcomers, multById);
    }

    const pick = lastPick();
    if (pick && inCart.some((it) => num(it.variant_id) === pick)) return pick;

    return highestOf(inCart, multById);
  }

  function buildExclusiveUpdates(winnerId, ids) {
    return ids.reduce((acc, id) => {
      acc[id] = winnerId && num(id) === num(winnerId) ? 1 : 0;
      return acc;
    }, {});
  }

  function needsFix(boostersInCart, winnerId) {
    return boostersInCart.some((it) => {
      const vid = num(it.variant_id);
      const qty = num(it.quantity);
      if (vid !== num(winnerId) && qty > 0) return true;
      return vid === num(winnerId) && qty !== 1;
    });
  }

  async function enforceSingleBooster({ refreshUI = true, cart = null } = {}) {
    if (isNormalizing) return;
    const ids = boosterIds();
    if (!ids.length) return;

    isNormalizing = true;
    try {
      const current = cart || (await fetchCart());
      const boostersInCart = boostersIn(current, ids);

      if (!boostersInCart.length) {
        rememberSeen([]);
        if (refreshUI) clearActiveUI();
        return;
      }

      const winnerId = chooseWinnerVariantId(boostersInCart, multiplierById());
      setLastPick(winnerId);

      if (!needsFix(boostersInCart, winnerId)) {
        rememberSeen([winnerId]);
        if (refreshUI) setActiveUI(winnerId);
        return;
      }

      if (refreshUI) setActiveUI(winnerId);
      await cartUpdate(buildExclusiveUpdates(winnerId, ids));
      if (refreshUI) setActiveUI(winnerId);
    } catch (err) {
      console.error('[ff-multipliers][enforce]', err);
    } finally {
      isNormalizing = false;
    }
  }

  /** Returns the cart after cleanup so the caller need not fetch it again. */
  async function normalizeTenXLines(cart) {
    const needle = TEN_X_TITLE_MATCH.trim().toLowerCase();
    if (!needle) return cart;

    try {
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const tenX = items.filter((item) =>
        (item.product_title || item.title || '').toString().toLowerCase().includes(needle)
      );
      if (!tenX.length) return cart;

      const isDiscounted = (item) =>
        (Array.isArray(item.line_level_discount_allocations) &&
          item.line_level_discount_allocations.length > 0) ||
        num(item.line_price) === 0;

      const discounted = tenX.filter(isDiscounted);
      const fullPrice = tenX.filter((item) => !isDiscounted(item) && num(item.line_price) > 0);
      if (!discounted.length || !fullPrice.length) return cart;

      const updates = {};
      for (const item of fullPrice) {
        if (item.variant_id) updates[item.variant_id] = 0;
      }

      if (!Object.keys(updates).length) return cart;
      return await cartUpdate(updates);
    } catch (err) {
      console.error('[ff-multipliers][10x-normalize]', err);
      return cart;
    }
  }

  /* --------------------------------------------------------------- events */

  async function onBoostClick(e) {
    const btn = e.target.closest(BOOST_SELECTOR);
    if (!btn) return;
    e.preventDefault();

    const ids = boosterIds();
    const variantId = num(btn.dataset.variantId);
    if (!variantId || !ids.includes(variantId)) return;

    const turningOff = btn.classList.contains('selected');
    const nextWinner = turningOff ? null : variantId;

    setLastPick(nextWinner);

    // Optimistic UI; the section re-render is the source of truth.
    setActiveUI(nextWinner);
    previewEntries(nextWinner ? btn.dataset.multiplier : 1);

    clearError(btn);
    setBusy(btn, true);

    try {
      await cartUpdate(buildExclusiveUpdates(nextWinner, ids));
    } catch (err) {
      console.error('[ff-multipliers][set]', err);
      showError(btn, 'Could not update your booster. Please try again.');
      enforceSingleBooster({ refreshUI: true });
    } finally {
      // The drawer usually re-rendered, so this button may already be gone.
      setBusy(btn, false);
      setPending(false);
    }
  }

  /**
   * A booster added elsewhere (product page, app) becomes the newest pick.
   * Returns its variant id so the caller can apply the rule without reading
   * the cart first.
   */
  async function addedBoosterId(res) {
    try {
      const data = await res.clone().json();
      const items = Array.isArray(data?.items) ? data.items : [data];
      const ids = boosterIds();
      const added = items.map((it) => num(it?.variant_id)).filter((id) => ids.includes(id));
      return added.length ? added[added.length - 1] : 0;
    } catch (err) {
      /* not JSON, or not a cart payload; the full enforcement still runs */
      return 0;
    }
  }

  /** The newest booster replaces whatever was there, in one request. */
  async function adoptBooster(variantId) {
    if (isNormalizing) return;
    isNormalizing = true;
    try {
      setLastPick(variantId);
      setActiveUI(variantId);
      await cartUpdate(buildExclusiveUpdates(variantId, boosterIds()));
      setActiveUI(variantId);
    } catch (err) {
      console.error('[ff-multipliers][adopt]', err);
    } finally {
      isNormalizing = false;
    }
  }

  /** Re-enforce when any other script changes the cart. */
  function installFetchGuard() {
    const origFetch = window.fetch;
    if (origFetch.__ffMultipliersWrapped) return;

    const wrapped = async function (...args) {
      const res = await origFetch.apply(this, args);

      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : input?.url || '';
        const headers = args[1]?.headers || {};
        const tagged = Boolean(headers[ENFORCE_HEADER] || headers[ENFORCE_HEADER.toLowerCase()]);

        if (CART_ENDPOINT_RE.test(url) && !tagged) {
          const added = CART_ADD_RE.test(url) && res.ok ? await addedBoosterId(res) : 0;
          if (added) queueMicrotask(() => adoptBooster(added));
          else queueMicrotask(() => enforceSingleBooster({ refreshUI: true }));
        }
      } catch (err) {
        /* never block the original request */
      }

      return res;
    };

    wrapped.__ffMultipliersWrapped = true;
    window.fetch = wrapped;
  }

  function installSubmitGuard() {
    const form = document.getElementById('cart');
    if (!form || form.__ffSubmitBound) return;
    form.__ffSubmitBound = true;
    form.addEventListener('submit', () => enforceSingleBooster({ refreshUI: false }));
  }

  async function init() {
    document.addEventListener('click', onBoostClick);
    installFetchGuard();
    installSubmitGuard();

    window.addEventListener('pageshow', (e) => {
      if (e.persisted) enforceSingleBooster({ refreshUI: true });
    });

    if (!boosterIds().length) return;

    try {
      // One cart read covers both the legacy 10X cleanup and the booster rule.
      const cart = await normalizeTenXLines(await fetchCart());
      await enforceSingleBooster({ refreshUI: true, cart });
    } catch (err) {
      console.error('[ff-multipliers][init]', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /** Optional external controller, kept for backwards compatibility. */
  window.FFSetExclusiveMultiplier = async (variantId) => {
    const ids = boosterIds();
    const vid = variantId ? num(variantId) : null;
    if (vid && !ids.includes(vid)) return;

    setLastPick(vid);
    await cartUpdate(buildExclusiveUpdates(vid, ids));
  };
})();
